import { APP_INVENTORY_API_BASE } from "../../../../config/api";
/**
 * LeasesList — Leases page
 *
 * Route: /inventory/leases
 *
 * Shows all lease agreements for the org — both as lessor and lessee.
 * Filterable by status.
 * Row click → navigate to asset detail.
 * Terminate action directly from the list.
 *
 * APIs:
 *   GET  /api/v1/inventory/leases
 *   POST /api/v1/inventory/leases/:id/terminate
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  Chip,
  Alert,
  Skeleton,
  Stack,
  TextField,
  MenuItem,
  useTheme,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import HandshakeIcon from "@mui/icons-material/Handshake";
import CancelIcon from "@mui/icons-material/Cancel";
import { Search, Close } from "@mui/icons-material";
import FilterListIcon from "@mui/icons-material/FilterList";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useAuth } from "../../../../auth/useAuth";

const API_BASE = `${APP_INVENTORY_API_BASE}`;

const STATUS_STYLES = {
  ACTIVE: { color: "#065f46", bg: "#ecfdf5" },
  EXPIRED: { color: "#6b7280", bg: "#f3f4f6" },
  TERMINATED: { color: "#dc2626", bg: "#fff5f5" },
};

// ─── Terminate dialog ─────────────────────────────────────────────────────────

function TerminateDialog({
  open,
  lease,
  token,
  activeOrg,
  onClose,
  onTerminated,
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const handleTerminate = async () => {
    if (!reason.trim()) {
      setError("Termination reason is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_BASE}/leases/${lease.id}/terminate`,
        { termination_reason: reason.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
          },
        },
      );
      onTerminated(res.data.lease);
    } catch (err) {
      setError(err.response?.data?.error || "Termination failed");
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle fontWeight={700}>Terminate lease?</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText sx={{ mb: 2 }}>
          Asset{" "}
          <strong>
            {lease?.asset_serial_number ||
              lease?.inventory_asset_id?.slice(0, 8)}
          </strong>{" "}
          — lease ends <strong>{lease?.end_date}</strong>. This cannot be
          undone.
        </DialogContentText>
        <TextField
          label="Termination reason"
          size="small"
          fullWidth
          multiline
          rows={2}
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Customer request, contract breach…"
          error={!!error && !reason.trim()}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disableElevation
          onClick={handleTerminate}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <CancelIcon />
            )
          }
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {submitting ? "Terminating…" : "Terminate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Custom toolbar ───────────────────────────────────────────────────────────

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Box display="flex" gap={1}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport csvOptions={{ fileName: "leases" }} />
      </Box>
    </GridToolbarContainer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function LeasesList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { permissions, activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leases, setLeases] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [terminateTarget, setTerminateTarget] = useState(null);
  const [successAlert, setSuccessAlert] = useState("");
  const [stats, setStats] = useState({
    active: 0,
    expired: 0,
    terminated: 0,
    loading: true,
  });

  const [searchQuery, setSearchQuery] = useState("");

  const filteredLeases = leases.filter((row) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return Object.values(row).some((val) =>
      String(val ?? "")
        .toLowerCase()
        .includes(q),
    );
  });

  const hasPermission = (p) => {
    if (!permissions || permissions.size === 0) return false;
    if (permissions.has("*")) return true;
    return permissions.has(p);
  };

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  const showSuccess = (msg) => {
    setSuccessAlert(msg);
    setTimeout(() => setSuccessAlert(""), 5000);
  };

  // ── Fetch leases ──
  const fetchLeases = useCallback(async () => {
    if (!hasPermission("inventory.lease.view")) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/leases`, {
        headers: authHeaders,
        params: {
          page: paginationModel.page + 1,
          per_page: paginationModel.pageSize,
          ...(statusFilter && { lease_status: statusFilter }),
        },
      });
      setLeases(res.data.data || []);
      setRowCount(res.data.meta?.pagination?.total_items || 0);
      setError(null);
    } catch {
      setError("Failed to load leases.");
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg, paginationModel, statusFilter]);

  useEffect(() => {
    fetchLeases();
  }, [fetchLeases]);

  // ── Fetch stats ──
  const fetchStats = useCallback(() => {
    if (!hasPermission("inventory.lease.view")) return;
    Promise.all([
      axios.get(`${API_BASE}/leases`, {
        headers: authHeaders,
        params: { per_page: 1, lease_status: "ACTIVE" },
      }),
      axios.get(`${API_BASE}/leases`, {
        headers: authHeaders,
        params: { per_page: 1, lease_status: "EXPIRED" },
      }),
      axios.get(`${API_BASE}/leases`, {
        headers: authHeaders,
        params: { per_page: 1, lease_status: "TERMINATED" },
      }),
    ])
      .then(([a, e, t]) =>
        setStats({
          active: a.data.meta?.pagination?.total_items ?? 0,
          expired: e.data.meta?.pagination?.total_items ?? 0,
          terminated: t.data.meta?.pagination?.total_items ?? 0,
          loading: false,
        }),
      )
      .catch(() => setStats((prev) => ({ ...prev, loading: false })));
  }, [token, activeOrg]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleTerminated = (updatedLease) => {
    setTerminateTarget(null);
    setLeases((prev) =>
      prev.map((l) => (l.id === updatedLease.id ? updatedLease : l)),
    );
    fetchStats();
    showSuccess("Lease terminated successfully.");
  };

  // ── Columns ──
  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "lease_status",
      headerName: "Status",
      width: 110,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const s = STATUS_STYLES[params.value] || {
          color: "#374151",
          bg: "#f3f4f6",
        };
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Chip
              label={params.value}
              size="small"
              sx={{
                bgcolor: s.bg,
                color: s.color,
                fontWeight: 600,
                borderRadius: "6px",
                fontSize: 11,
                height: 24,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "asset_serial_number",
      headerName: "Asset",
      flex: 1.1,
      minWidth: 140,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            color="primary.main"
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {params.value || params.row.inventory_asset_id?.slice(0, 8) + "…"}
          </Typography>
          {params.row.asset_type && (
            <Typography
              variant="caption"
              noWrap
              color="text.secondary"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.row.asset_type}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "lessor_org_id",
      headerName: "Lessor",
      flex: 1,
      minWidth: 120,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontFamily: "monospace",
              fontSize: 11,
              color: "text.secondary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {params.value?.slice(0, 8)}…
          </Typography>
        </Box>
      ),
    },
    {
      field: "lessee_org_id",
      headerName: "Lessee",
      flex: 1,
      minWidth: 120,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontFamily: "monospace",
              fontSize: 11,
              color: "text.secondary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {params.value?.slice(0, 8)}…
          </Typography>
        </Box>
      ),
    },
    {
      field: "start_date",
      headerName: "Start",
      width: 100,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            color="text.secondary"
            sx={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            {params.value || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "end_date",
      headerName: "End",
      width: 100,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            color="text.secondary"
            sx={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            {params.value || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "rate",
      headerName: "Rate",
      width: 130,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            {params.row.currency}{" "}
            {parseFloat(params.value || 0).toLocaleString("en-IN")}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.5 }}
            >
              /{params.row.billing_cycle?.toLowerCase()}
            </Typography>
          </Typography>
        </Box>
      ),
    },
    {
      field: "_actions",
      headerName: "Action",
      width: 90,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="View asset">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/inventory/assets/${params.row.inventory_asset_id}`,
                  );
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {params.row.lease_status === "ACTIVE" &&
              hasPermission("inventory.lease.terminate") && (
                <Tooltip title="Terminate">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTerminateTarget(params.row);
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
          </Stack>
        </Box>
      ),
    },
  ];

  if (!hasPermission("inventory.lease.view")) {
    return (
      <Box p={4}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          You do not have permission to view leases.
        </Alert>
      </Box>
    );
  }

  const statCards = [
    {
      title: "Active leases",
      value: stats.active,
      icon: <HandshakeIcon sx={{ color: "#5eead4" }} />,
      bg: "rgba(20, 184, 166, 0.12)",
      border: "rgba(20, 184, 166, 0.25)",
      status: "ACTIVE",
    },
    {
      title: "Expired",
      value: stats.expired,
      icon: <HandshakeIcon sx={{ color: "#94a3b8" }} />,
      bg: "rgba(148, 163, 184, 0.12)",
      border: "rgba(148, 163, 184, 0.25)",
      status: "EXPIRED",
    },
    {
      title: "Terminated",
      value: stats.terminated,
      icon: <HandshakeIcon sx={{ color: "#fca5a5" }} />,
      bg: "rgba(239, 68, 68, 0.12)",
      border: "rgba(239, 68, 68, 0.25)",
      status: "TERMINATED",
    },
  ];

  return (
    <Box
      sx={{
        margin: "0 auto",
        p: {
          xs: 2,
          md: 3,
          background:
            "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #F8FBFF 0%, #EEF4F8 100%)",
        },
      }}
    >
      <Box
        sx={{
          border: "1px solid rgba(148, 163, 184, 0.18)",
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          borderRadius: 3,
          p: 3,
          mb: 4,
        }}
      >
        {/* ── Page header ── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          mb={3}
        >
          <Box>
           <Typography variant="h4" fontWeight={700} sx={{ color: "#e2f8f5" ,fontSize:{xs:28,md:40} }}>
              Leases
            </Typography>
            <Typography
              variant="body2"
              mt={0.3}
              sx={{ color: "rgba(203, 213, 225, 0.80)" }}
            >
              Financial leasing agreements across all assets.
            </Typography>
          </Box>
        </Stack>

        {/* ── Divider ── */}
        <Box
          sx={{ borderBottom: "1px solid rgba(148, 163, 184, 0.15)", mb: 3 }}
        />

        {/* ── Alerts ── */}
        <Collapse in={!!successAlert}>
          <Alert
            severity="success"
            onClose={() => setSuccessAlert("")}
            sx={{
              mb: 3,
              borderRadius: 2,
              background: "rgba(20, 184, 166, 0.12)",
              border: "1px solid rgba(20, 184, 166, 0.28)",
              color: "#5eead4",
              "& .MuiAlert-icon": { color: "#5eead4" },
              "& .MuiIconButton-root": { color: "#5eead4" },
            }}
          >
            {successAlert}
          </Alert>
        </Collapse>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              background: "rgba(239, 68, 68, 0.12)",
              border: "1px solid rgba(239, 68, 68, 0.28)",
              color: "#fca5a5",
              "& .MuiAlert-icon": { color: "#fca5a5" },
            }}
          >
            {error}
          </Alert>
        )}

        {/* ── Stat cards ── */}
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
          gap={2}
        >
          {stats.loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: "1px solid rgba(148, 163, 184, 0.15)",
                    background: "rgba(255, 255, 255, 0.04)",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={52}
                    height={52}
                    sx={{
                      mr: 2,
                      borderRadius: 2,
                      bgcolor: "rgba(148, 163, 184, 0.10)",
                    }}
                  />
                  <Box flex={1}>
                    <Skeleton
                      variant="text"
                      width="60%"
                      sx={{ bgcolor: "rgba(148, 163, 184, 0.10)" }}
                    />
                    <Skeleton
                      variant="text"
                      width="40%"
                      height={32}
                      sx={{ bgcolor: "rgba(148, 163, 184, 0.10)" }}
                    />
                  </Box>
                </Card>
              ))
            : statCards.map((s, i) => (
                <Card
                  key={i}
                  elevation={0}
                  onClick={() => {
                    setStatusFilter(s.status);
                    setPaginationModel((p) => ({ ...p, page: 0 }));
                  }}
                  sx={{
                    borderRadius: 2.5,
                    border: "1px solid rgba(148, 163, 184, 0.15)",
                    background: "rgba(255, 255, 255, 0.04)",
                    p: 2,
                    cursor: "pointer",
                    transition: "background 0.2s, border-color 0.2s",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.08)",
                      borderColor: "rgba(148, 163, 184, 0.30)",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        bgcolor: s.bg,
                        border: `1px solid ${s.border}`,
                        mr: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ color: "rgba(203, 213, 225, 0.75)" }}
                      >
                        {s.title}
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ color: "#e2f8f5" }}
                      >
                        {s.value.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
        </Box>
      </Box>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Filter bar */}
        <Box
          p={2}
          borderBottom="1px solid rgba(148, 163, 184, 0.18)"
          display="flex"
          gap={2}
          flexWrap="wrap"
          alignItems="center"
          sx={{
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          }}
        >
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon
                    fontSize="small"
                    sx={{ color: "rgba(203, 213, 225, 0.60)" }}
                  />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="EXPIRED">Expired</MenuItem>
            <MenuItem value="TERMINATED">Terminated</MenuItem>
          </TextField>

          {statusFilter && (
            <Button
              size="small"
              onClick={() => setStatusFilter("")}
              sx={{
                textTransform: "none",
                fontSize: 12,
                height: 38,
                color: "#fca5a5",
                borderColor: "rgba(239, 68, 68, 0.35)",
                background: "rgba(239, 68, 68, 0.10)",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                borderRadius: 1,
                "&:hover": {
                  borderColor: "rgba(239, 68, 68, 0.55)",
                  background: "rgba(239, 68, 68, 0.18)",
                },
              }}
            >
              Clear filter
            </Button>
          )}

          {/* Search bar — right side */}
          <TextField
            size="small"
            placeholder="Search leases..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            sx={{
              width: { xs: "100%", sm: 260 },
              ml: { xs: 0, sm: "auto" },
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "rgba(148, 163, 184, 0.55)",
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    sx={{ color: "rgba(203, 213, 225, 0.60)" }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery("");
                      setPaginationModel((p) => ({ ...p, page: 0 }));
                    }}
                    sx={{
                      color: "rgba(203, 213, 225, 0.60)",
                      p: 0.3,
                      "&:hover": {
                        color: "#e2f8f5",
                        background: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* 
  <Box sx={{ ml: "auto" }}>
    <Typography variant="caption" color="text.secondary">
      {loading
        ? "Loading…"
        : `${rowCount.toLocaleString()} lease${rowCount !== 1 ? "s" : ""}`}
    </Typography>
  </Box> */}
        </Box>

        <Box
          sx={{
            height: leases.length === 0 && !loading ? 320 : 580,
            width: "100%",
          }}
        >
          <DataGrid
            rows={filteredLeases}
            rowCount={rowCount}
            loading={loading}
            paginationMode="server"
            pageSizeOptions={[15, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            columns={columns}
            getRowId={(row) => row.id}
            onRowClick={(params) => {
              if (params.field === "_actions") return;
              navigate(`/inventory/assets/${params.row.inventory_asset_id}`);
            }}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: () => (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  py={6}
                >
                  <HandshakeIcon
                    sx={{ fontSize: 52, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.secondary"
                    gutterBottom
                  >
                    {statusFilter
                      ? "No leases match this filter"
                      : "No leases created yet"}
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    Create leases from the asset detail page
                  </Typography>
                </Box>
              ),
            }}
            disableRowSelectionOnClick
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f8fafc",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "text.secondary",
                fontSize: 12,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#334155" : "#f8fafc",
                cursor: "pointer",
              },
              "& .MuiDataGrid-cell:focus": { outline: "none" },
            }}
          />
        </Box>
      </Card>

      {terminateTarget && (
        <TerminateDialog
          open={!!terminateTarget}
          lease={terminateTarget}
          token={token}
          activeOrg={activeOrg}
          onClose={() => setTerminateTarget(null)}
          onTerminated={handleTerminated}
        />
      )}
    </Box>
  );
}
